import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription, filter, takeUntil } from 'rxjs';

import { AuthService } from '../../../auth';
import { LittleTiffApiService } from '../../../core/api/services';
import { LittleTiffActionLink, LittleTiffAskResponse, LittleTiffStatus, Permissions, UserModel } from '../../../core/api/models';

interface LittleTiffFeedbackContext {
  question: string;
  currentPath: string;
  pageTitle: string;
  patientId: number | null;
  contextJson: string;
}

interface LittleTiffMessage {
  role: 'user' | 'assistant';
  text: string;
  steps?: string[];
  actions?: LittleTiffActionLink[];
  warnings?: string[];
  modelId?: string | null;
  sourcesUsed?: string[];
  feedback?: LittleTiffFeedbackContext | null;
  feedbackSaved?: boolean;
  showCorrection?: boolean;
  correction?: string;
  feedbackStatus?: string;
}

const MAX_STORED_MESSAGES = 40;
const MAX_API_HISTORY = 6;
const MAX_SNAPSHOT_ITEMS = 40;
const MAX_SNAPSHOT_ITEM_LENGTH = 80;
const DRAG_THRESHOLD_PX = 5;
const VIEWPORT_MARGIN_PX = 8;
const PANEL_WIDTH_PX = 380;
const PANEL_HEIGHT_PX = 520;
// Little Tiff sits just left of the first of these header buttons until the user drags it somewhere else.
const DEFAULT_ANCHOR_SELECTORS = ['#sideMenuButton'];

@Component({
  selector: 'app-little-tiff',
  templateUrl: './little-tiff.component.html',
  styleUrl: './little-tiff.component.css',
  standalone: false,
})
export class LittleTiffComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('root') root?: ElementRef<HTMLDivElement>;

  canUse = false;
  isOpen = false;
  isLoading = false;
  statusRequested = false;
  status: LittleTiffStatus | null = null;
  statusMessage = '';
  selectedModelId = '';
  input = '';
  messages: LittleTiffMessage[] = [];
  left: number | null = null;
  top: number | null = null;
  panelBelow = true;
  panelMaxHeight = PANEL_HEIGHT_PX;
  panelAlignLeft = false;
  isDragging = false;

  private drag: { pointerId: number; startX: number; startY: number; offsetX: number; offsetY: number } | null = null;
  private suppressNextClick = false;
  private savedPosition: { left: number; top: number } | null = null;
  private userChanged = new Subject<void>();
  private anchorObserver?: ResizeObserver;
  private watchedAnchor?: HTMLElement;

  private userId = 0;
  private subscriptions: Subscription[] = [];

  constructor(
    private authService: AuthService,
    private littleTiffApi: LittleTiffApiService,
    private router: Router,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.applyUser(this.authService.userInfo);
    this.subscriptions.push(
      this.authService.userInfo$.subscribe((user) => this.applyUser(user)),
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.scrollToBottom();
        this.schedulePosition();
      })
    );
    this.schedulePosition();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.anchorObserver?.disconnect();
  }

  get hasModels(): boolean {
    return !!this.status?.Enabled && !!this.selectedModelId;
  }

  onToggleClick(): void {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    this.toggle();
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || (event.target as HTMLElement).closest('.little-tiff-close')) return;
    const rect = this.root?.nativeElement.getBoundingClientRect();
    if (!rect) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    if (!this.isDragging &&
      Math.abs(event.clientX - this.drag.startX) < DRAG_THRESHOLD_PX &&
      Math.abs(event.clientY - this.drag.startY) < DRAG_THRESHOLD_PX) return;

    this.isDragging = true;
    this.setPosition(event.clientX - this.drag.offsetX, event.clientY - this.drag.offsetY);
    event.preventDefault();
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    this.drag = null;
    if (!this.isDragging) return;

    this.isDragging = false;
    this.suppressNextClick = true;
    setTimeout(() => (this.suppressNextClick = false));
    if (this.left !== null && this.top !== null) {
      this.savedPosition = { left: this.left, top: this.top };
      try {
        localStorage.setItem(this.positionStorageKey, JSON.stringify(this.savedPosition));
      } catch {
        // localStorage unavailable
      }
    }
  }

  @HostListener('window:resize')
  applyPosition(): void {
    if (this.savedPosition) {
      this.setPosition(this.savedPosition.left, this.savedPosition.top);
      return;
    }

    const size = this.getRootSize();
    const anchor = DEFAULT_ANCHOR_SELECTORS
      .map((selector) => document.querySelector<HTMLElement>(selector))
      .find((el) => !!el && !!el.offsetParent);
    if (anchor) {
      this.watchAnchor(anchor);
      const rect = anchor.getBoundingClientRect();
      this.setPosition(rect.left - size.width - 12, rect.top + rect.height / 2 - size.height / 2);
    } else {
      this.setPosition(window.innerWidth - size.width - 16, VIEWPORT_MARGIN_PX);
    }
  }

  private setPosition(left: number, top: number): void {
    const size = this.getRootSize();
    this.left = Math.min(Math.max(left, VIEWPORT_MARGIN_PX), Math.max(VIEWPORT_MARGIN_PX, window.innerWidth - size.width - VIEWPORT_MARGIN_PX));
    this.top = Math.min(Math.max(top, VIEWPORT_MARGIN_PX), Math.max(VIEWPORT_MARGIN_PX, window.innerHeight - size.height - VIEWPORT_MARGIN_PX));
    // Open the panel on the side with more room and never taller than that room.
    const spaceBelow = window.innerHeight - (this.top + size.height) - 10 - VIEWPORT_MARGIN_PX;
    const spaceAbove = this.top - 10 - VIEWPORT_MARGIN_PX;
    this.panelBelow = spaceBelow >= Math.min(PANEL_HEIGHT_PX, spaceAbove);
    this.panelMaxHeight = Math.max(240, Math.min(PANEL_HEIGHT_PX, this.panelBelow ? spaceBelow : spaceAbove));
    this.panelAlignLeft = this.left + size.width < PANEL_WIDTH_PX + VIEWPORT_MARGIN_PX;
  }

  // Header buttons move as their text, badges and the page scrollbar load, so follow them until the user drags Little Tiff away.
  private watchAnchor(anchor: HTMLElement): void {
    if (this.watchedAnchor === anchor || typeof ResizeObserver === 'undefined') return;
    this.anchorObserver?.disconnect();
    this.watchedAnchor = anchor;
    this.anchorObserver = new ResizeObserver(() => this.zone.run(() => {
      if (!this.savedPosition) this.applyPosition();
    }));
    const toggle = this.root?.nativeElement.querySelector<HTMLElement>('.little-tiff-toggle');
    [document.body, anchor.parentElement, ...Array.from(anchor.parentElement?.children ?? [anchor]), toggle]
      .forEach((el) => el && this.anchorObserver!.observe(el));
  }

  private getRootSize(): { width: number; height: number } {
    const toggle = this.root?.nativeElement.querySelector<HTMLElement>('.little-tiff-toggle');
    const rect = toggle?.getBoundingClientRect();
    return { width: rect?.width || 56, height: rect?.height || 56 };
  }

  private get positionStorageKey(): string {
    return 'littleTiff.position.' + this.userId;
  }

  private loadSavedPosition(): void {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.positionStorageKey) || 'null');
      this.savedPosition = parsed && typeof parsed.left === 'number' && typeof parsed.top === 'number' ? parsed : null;
    } catch {
      this.savedPosition = null;
    }
  }

  private schedulePosition(): void {
    // The header buttons render after navigation, so position once they are on screen.
    setTimeout(() => this.applyPosition(), 300);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.statusRequested) {
      this.statusRequested = true;
      this.loadStatus();
    }
    this.scrollToBottom();
  }

  send(): void {
    const text = (this.input || '').trim();
    if (!text || this.isLoading || !this.hasModels) return;

    const currentPath = window.location.pathname || '/';
    const pageTitle = document.title || '';
    const patientId = this.getPatientIdFromUrl();
    const history = this.messages.slice(-MAX_API_HISTORY).map((m) => ({ Role: m.role, Text: m.text }));

    this.input = '';
    this.messages.push({ role: 'user', text });
    this.saveHistory();
    this.isLoading = true;
    this.scrollToBottom();

    this.littleTiffApi.Ask({
      body: {
        Message: text,
        CurrentPath: currentPath,
        PageTitle: pageTitle,
        PatientId: patientId,
        ModelId: this.selectedModelId,
        History: history,
        PageSnapshot: this.buildPageSnapshot(),
      },
    }).pipe(takeUntil(this.userChanged)).subscribe({
      next: (response: LittleTiffAskResponse) => {
        this.isLoading = false;
        this.messages.push({
          role: 'assistant',
          text: response.Answer || '',
          steps: response.Steps || [],
          actions: response.Actions || [],
          warnings: response.Warnings || [],
          modelId: response.ModelId,
          sourcesUsed: response.SourcesUsed || [],
          feedback: this.status?.CanGiveFeedback
            ? { question: text, currentPath, pageTitle, patientId, contextJson: response.FeedbackContextJson || '' }
            : null,
        });
        this.saveHistory();
        this.scrollToBottom();
      },
      error: (error) => {
        this.isLoading = false;
        this.messages.push({ role: 'assistant', text: this.getErrorText(error, 'Little Tiff could not answer right now. Please try again.') });
        this.saveHistory();
        this.scrollToBottom();
      },
    });
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  clearHistory(): void {
    if (!window.confirm('Clear Little Tiff context and start a new conversation?')) return;
    this.messages = [];
    this.saveHistory();
  }

  openAction(action: LittleTiffActionLink): void {
    if (!action.Url) return;
    this.router.navigateByUrl(action.Url);
  }

  submitFeedback(message: LittleTiffMessage, rating: 'good' | 'wrong'): void {
    const correction = (message.correction || '').trim();
    if (rating === 'wrong' && !correction) {
      message.feedbackStatus = 'Add the correct answer first.';
      return;
    }

    const feedback = message.feedback;
    if (!feedback) return;

    message.feedbackStatus = 'Saving...';
    this.littleTiffApi.Feedback({
      body: {
        Rating: rating,
        Question: feedback.question,
        Answer: message.text,
        CorrectAnswer: rating === 'wrong' ? correction : null,
        ContextJson: feedback.contextJson || null,
        CurrentPath: feedback.currentPath,
        PageTitle: feedback.pageTitle,
        PatientId: feedback.patientId,
        ModelId: message.modelId,
        Steps: message.steps,
        Actions: message.actions,
        Warnings: message.warnings,
        SourcesUsed: message.sourcesUsed,
      },
    }).pipe(takeUntil(this.userChanged)).subscribe({
      next: () => {
        message.feedbackSaved = true;
        message.showCorrection = false;
        message.feedbackStatus = 'Feedback saved.';
        this.saveHistory();
      },
      error: (error) => {
        message.feedbackStatus = this.getErrorText(error, 'Could not save feedback.');
      },
    });
  }

  private applyUser(user: UserModel | null): void {
    const userId = user?.ID || 0;
    this.canUse = !!user?.Permissions?.includes(Permissions.LittleTiff_Use);
    if (userId !== this.userId) {
      this.userId = userId;
      // Drop replies still in flight for the previous user so they never land in the new user's conversation.
      this.userChanged.next();
      this.isLoading = false;
      this.statusRequested = false;
      this.status = null;
      this.messages = this.loadHistory();
      this.loadSavedPosition();
      this.schedulePosition();
    }
  }

  private loadStatus(): void {
    this.statusMessage = 'Loading models...';
    this.littleTiffApi.Status().pipe(takeUntil(this.userChanged)).subscribe({
      next: (status) => {
        this.status = status;
        const models = status.Models || [];
        this.selectedModelId =
          models.find((m) => m.ModelId === this.selectedModelId)?.ModelId ||
          models.find((m) => m.ModelId === status.ModelId)?.ModelId ||
          models[0]?.ModelId ||
          '';
        this.statusMessage = this.hasModels ? '' : 'AI access is unavailable. Ask an administrator to check your AI usage limit.';
      },
      error: (error) => {
        this.statusRequested = false;
        this.statusMessage = this.getErrorText(error, 'Could not load AI models.');
      },
    });
  }

  // Spark routes carry the selected client id: /staff/{id}/..., /staff/home/{id}/..., /staff/preview/{id}, /supervisor/{id}/..., /graphs/{id}/...
  private getPatientIdFromUrl(): number | null {
    const match = /^\/(?:staff\/(?:home\/|preview\/)?|supervisor\/|graphs\/)(\d+)/.exec(window.location.pathname || '');
    return match ? parseInt(match[1], 10) : null;
  }

  // Visible structure of the page (no field values) so answers can name the real buttons and tabs.
  private buildPageSnapshot(): string {
    const sections = [
      { name: 'Headings', selector: 'h1, h2, h3, h4, legend, .p-card-title, .p-dialog-title' },
      { name: 'Tabs', selector: '[role="tab"], .p-tabview-nav a' },
      { name: 'Buttons and links', selector: 'button, a.p-button, a[routerLink], a[href]' },
      { name: 'Field labels', selector: 'label' },
      { name: 'Table columns', selector: 'thead th' },
    ];
    const lines = ['Title: ' + (document.title || '')];

    for (const section of sections) {
      const seen = new Set<string>();
      const items: string[] = [];
      document.querySelectorAll<HTMLElement>(section.selector).forEach((el) => {
        if (items.length >= MAX_SNAPSHOT_ITEMS || !el.offsetParent || el.closest('app-little-tiff')) return;
        let text = (el.getAttribute('aria-label') || el.innerText || el.getAttribute('title') || '').replace(/\s+/g, ' ').trim();
        if (text.length > MAX_SNAPSHOT_ITEM_LENGTH) text = text.substring(0, MAX_SNAPSHOT_ITEM_LENGTH) + '...';
        if (!text || seen.has(text.toLowerCase())) return;
        seen.add(text.toLowerCase());
        items.push(text);
      });

      if (items.length) lines.push(section.name + ': ' + items.join(' | '));
    }

    return lines.join('\n');
  }

  private getErrorText(error: any, fallback: string): string {
    if (typeof error === 'string' && error) return error;
    return error?.Message || error?.message || fallback;
  }

  private get storageKey(): string {
    return 'littleTiff.chat.' + this.userId;
  }

  private loadHistory(): LittleTiffMessage[] {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private saveHistory(): void {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.messages.slice(-MAX_STORED_MESSAGES)));
    } catch {
      // sessionStorage full or unavailable
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
